/**
 * WebOverlayContainer
 * 
 * A container for overlays (modals, side menus, cards) on web that ensures
 * they are positioned within the app boundaries.
 * 
 * Uses the WebAppContext to get the exact position of the app container
 * and positions its children accordingly.
 * 
 * Features:
 * - Fixed positioning relative to viewport
 * - Constrained to app container boundaries
 * - Proper z-index layering
 * - Optional backdrop with click-to-dismiss
 */

import React from 'react';
import { useWebAppContainer } from './WebAppContainer';
import { WEB_APP_MAX_WIDTH, WEB_COLORS, Z_INDEX, ANIMATION_DURATION } from './constants';

interface WebOverlayContainerProps {
  children: React.ReactNode;
  visible: boolean;
  onBackdropPress?: () => void;
  showBackdrop?: boolean;
  zIndex?: number;
  pointerEvents?: 'auto' | 'none' | 'box-none';
}

const WebOverlayContainer: React.FC<WebOverlayContainerProps> = ({
  children,
  visible,
  onBackdropPress,
  showBackdrop = true,
  zIndex = Z_INDEX.OVERLAY_BACKDROP,
  pointerEvents = 'auto',
}) => {
  const { containerLeft, containerWidth } = useWebAppContainer();

  // Calculate the actual container position
  // If containerLeft is 0, calculate it based on window width
  const actualLeft = containerLeft || Math.max(0, (window.innerWidth - WEB_APP_MAX_WIDTH) / 2);
  const actualWidth = containerWidth || Math.min(window.innerWidth, WEB_APP_MAX_WIDTH);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: actualLeft,
        width: actualWidth,
        height: '100%',
        zIndex,
        pointerEvents: visible ? pointerEvents : 'none',
        overflow: 'hidden',
      }}
    >
      {/* Backdrop */}
      {showBackdrop && (
        <div
          onClick={onBackdropPress}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: WEB_COLORS.BACKDROP,
            opacity: visible ? 1 : 0,
            transition: `opacity ${ANIMATION_DURATION.OVERLAY_FADE}ms ease-in-out`,
            pointerEvents: visible && onBackdropPress ? 'auto' : 'none',
          }}
        />
      )}

      {/* Content */}
      {children}
    </div>
  );
};

export default WebOverlayContainer;

